import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { Hash } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'
import { PlatformGroupHeader } from './platform-group-header'

type HashtagRow = RouterOutput['analytics']['hashtagEffect']['byBucket'][number]

const BUCKET_ORDER: Record<string, number> = { none: 0, few: 1, medium: 2, heavy: 3 }

export function HashtagEffectCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.hashtagEffect.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  const grouped = data?.byBucket ? groupByPlatform(data.byBucket) : new Map()
  const isMultiPlatform = grouped.size > 1

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Hash size={14} className="text-muted-foreground" />
          Hashtag Effect
          <InfoTooltip>
            How hashtag count tracks engagement on each platform. Instagram tends to reward heavy
            use; LinkedIn often punishes it. Each platform is compared against its own baseline.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Need posts with varied hashtag counts on at least one platform — vary your usage to
            unlock this view.
          </p>
        )}

        {data && data.hasEnoughData && (
          <div className={cn(isMultiPlatform ? 'space-y-4' : 'space-y-3')}>
            {[...grouped.entries()].map(([platform, rows]) => {
              const ordered = [...rows].sort(
                (a, b) => (BUCKET_ORDER[a.id] ?? 99) - (BUCKET_ORDER[b.id] ?? 99),
              )
              return (
                <div key={platform}>
                  {isMultiPlatform && <PlatformGroupHeader platform={platform} />}
                  <Bars rows={ordered} />
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Bars({ rows }: { rows: HashtagRow[] }) {
  const max = Math.max(...rows.map((r) => r.avgEngagement))
  return (
    <div className="space-y-3">
      {rows.map((b) => {
        const widthPct = max > 0 ? (b.avgEngagement / max) * 100 : 0
        return (
          <div key={`${b.platform}-${b.id}`}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <HintLabel hint={b.hint} className="text-foreground font-medium">
                {b.label}
              </HintLabel>
              <span
                className={cn(
                  'font-medium',
                  b.isBaseline
                    ? 'text-muted-foreground'
                    : b.relativeBoost && b.relativeBoost > 0
                      ? 'text-emerald-600 dark:text-emerald-500'
                      : 'text-muted-foreground',
                )}>
                {b.isBaseline ? (
                  <HintLabel hint="Lowest-performing hashtag bucket on this platform.">
                    baseline
                  </HintLabel>
                ) : b.relativeBoost != null ? (
                  `+${(b.relativeBoost * 100).toFixed(0)}%`
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="bg-muted relative h-2 overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <div className="bg-muted mb-1 h-3 w-20 animate-pulse rounded" />
          <div className="bg-muted h-2 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}
